import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Dropdown } from '../../ui-kit';
import { IconCopy, IconCut, IconPaste, IconTrash } from '../../ui-kit/icons';
import { overlappingIntervals, roundToDay } from '../../../utils/schedules';
import AvailabilityCodeIcon from '../../general/AvailabilityCodeIcon';
import DutyFunctionIcon from '../../general/DutyFunctionIcon';
import moment from 'moment';
import {
  updateIntervalsInMemory,
  updateIntervalsInBackend,
  fetchObjects as fetchSchedule
} from '../../../state/modules/combined-schedules/operations';

import {
  operations as standbyDutyFunctionsOperations,
  selectors as standbyDutyFunctionsSelectors
} from '../../../state/modules/standby-duty-functions';

import {
  operations as availabilityCodeOperations,
  selectors as availabilityCodeSelectors
} from '../../../state/modules/availability-codes';

import ScheduleWarningsPopoverConnected from '../warnings/ScheduleWarningsPopoverConnected';

const emptyInterval = {
  exception_available: null,
  availability_code_id: null,
  comments: '',
  assigned_function_ids: []
};

const ContextMenu = ({
  availabilityCodes,
  dutyFunctions,
  endTime,
  fetchAvailabilityCodes,
  fetchDutyFunctions,
  fetchSchedule,
  groupId,
  notifyChanged,
  ownerId,
  ownerType,
  assignedFunctionIds,
  schedules,
  setLoading,
  show,
  startTime,
  updateIntervalsInBackend,
  updateIntervalsInMemory,
  xCoord,
  yCoord
}) => {
  // a list of intervals that are to be copied
  const [clipboard, setClipboard] = useState([]);
  // keeps track from which ownerId the interval will be cut when pasting, if
  // applicable
  const [cutOwnerId, setCutOwnerId] = useState(null);

  // when the context menu loads for the first time, load the availability codes
  // and duty functions
  useEffect(() => {
    fetchAvailabilityCodes({ params: { presentation_type: 'basic', group_ids: groupId } });
    fetchDutyFunctions({ params: { presentation_type: 'basic', group_ids: groupId } });
  }, []);

  const fetchInterval = () => {
    if (!ownerId || !ownerType || !startTime || !endTime) return;
    const [start_time, end_time] = roundToDay(startTime, endTime);
    fetchSchedule({ id: ownerId, type: ownerType, start_time, end_time });
  };

  // then any of the params change, get the interval. This is to get the skills
  // so we know which duty functions can be applied
  useEffect(fetchInterval, [startTime, endTime, ownerId, ownerType]);

  const updateInterval = async interval => {
    // order is important here.
    // First we set the assigned functions (if any) to ensure we apply the changes
    // to the standby duty schedule
    // then we spread the interval `...interval`
    // finally start_time and end_time are overwritten. This is important for copying
    if (assignedFunctionIds.length > 0) interval = { assigned_function_ids: assignedFunctionIds, ...interval };
    interval = { ...interval, start_time: startTime, end_time: endTime };

    await updateSchedule([interval]);
  };

  const updateSchedule = async intervals => {
    intervals = [intervals].flat();

    setLoading();
    updateIntervalsInMemory(ownerId, ownerType, intervals);
    try {
      await updateIntervalsInBackend({ ignoreWarnings: false });
    } finally {
      notifyChanged();
    }
  };

  const handleWarningPopoverUpdated = () => {
    notifyChangedCutInterval();
    notifyChanged();
    fetchInterval();
  };

  const schedule = (schedules[ownerType] || {})[ownerId] || {};
  const intervals = schedule.intervals || [];
  // get the overlapping intervals and cut them to size
  const overlapping = [...overlappingIntervals(intervals, startTime, endTime)];
  if (overlapping.length > 0) {
    const firstInterval = overlapping[0];
    const lastInterval = overlapping.at(-1);
    overlapping[0] = { ...firstInterval, start_time: startTime };
    overlapping[overlapping.length - 1] = { ...lastInterval, end_time: endTime };
  }
  const skill_ids = overlapping[0]?.skill_ids || [];

  const copyIntervals = () => {
    const toCopy = overlapping.map(i => {
      const {
        start_time,
        end_time,
        detailed_availability: { exception },
        availability_code_id,
        assigned_function_ids,
        comments
      } = i;

      return {
        start_time,
        end_time,
        exception_available: exception || null,
        availability_code_id: availability_code_id || null,
        assigned_function_ids,
        comments
      };
    });

    setClipboard(toCopy);
  };

  const cutInterval = () => {
    copyIntervals();
    setCutOwnerId(ownerId);
  };

  const notifyChangedCutInterval = () => {
    if (!cutOwnerId) return;

    // if we're cutting, we also need to update the block from which this
    // originated
    setCutOwnerId(null);
    if (clipboard.length === 0) return;

    const firstInterval = clipboard[0];
    const lastInterval = clipboard.at(-1);

    if (moment(firstInterval.start_time).isSame(startTime) && moment(lastInterval.end_time).isSame(endTime)) return;

    notifyChanged(firstInterval.start_time, lastInterval.end_time);
  };

  const pasteInterval = async () => {
    const firstInterval = clipboard[0];
    const lastInterval = clipboard.at(-1);

    const dayDiff = moment(startTime).diff(firstInterval.start_time, 'day');
    // shift all intervals by the difference between the clicked interval and
    // what's in the copy buffer
    const toCopy = clipboard.map(i => {
      let { start_time, end_time } = i;
      start_time = moment(start_time).add(dayDiff, 'day');
      end_time = moment(end_time).add(dayDiff, 'day');

      return { ...i, start_time, end_time };
    });

    if (cutOwnerId) {
      const interval = {
        start_time: firstInterval.start_time,
        end_time: lastInterval.end_time,
        ...emptyInterval
      };
      updateIntervalsInMemory(cutOwnerId, ownerType, [interval]);
    }
    await updateSchedule(toCopy);
    notifyChangedCutInterval();
  };

  return (
    <>
      <ScheduleWarningsPopoverConnected handleUpdated={handleWarningPopoverUpdated} handleDismissed={fetchInterval} />
      <Dropdown.ContextMenu x={xCoord} y={yCoord} show={show}>
        <AvailabilityCodeSubMenu availabilityCodes={availabilityCodes} updateInterval={updateInterval} />
        <DutyFunctionSubMenu skillIds={skill_ids} dutyFunctions={dutyFunctions} updateInterval={updateInterval} />
        <Dropdown.Divider />
        <Dropdown.Item onClick={copyIntervals}>
          <IconCopy fixedWidth />
          {I18n.t('copy')}
        </Dropdown.Item>
        <Dropdown.Item onClick={cutInterval}>
          <IconCut fixedWidth />
          {I18n.t('cut')}
        </Dropdown.Item>
        <PasteIntervalMenuItem
          startTime={startTime}
          endTime={endTime}
          skillIds={skill_ids}
          clipboard={clipboard}
          pasteInterval={pasteInterval}
          dutyFunctions={dutyFunctions}
        />
        <Dropdown.Divider />
        <ClearIntervalMenuItem updateInterval={updateInterval} />
      </Dropdown.ContextMenu>
    </>
  );
};

const PasteIntervalMenuItem = ({ startTime, endTime, clipboard, pasteInterval, dutyFunctions, skillIds }) => {
  const clipboardEmpty = clipboard.length === 0;
  const firstInterval = clipboard[0] || {};
  const lastInterval = clipboard.at(-1) || {};
  const clipboardStartTime = firstInterval.start_time;
  const clipboardEndTime = lastInterval.end_time;

  // two allowable scenarios for pasting
  // 1. start and end times are exactly the same, but the date may be different
  // 2. [PENDING] from one full duty block to another full duty block
  const scenario1Allowed =
    !clipboardEmpty &&
    moment(clipboardStartTime).hour() == moment(startTime).hour() &&
    moment(clipboardStartTime).minute() == moment(startTime).minute() &&
    moment(clipboardEndTime).hour() == moment(endTime).hour() &&
    moment(clipboardEndTime).minute() == moment(endTime).minute();

  const clipboardFunctionIds = new Set(clipboard.flatMap(i => i.assigned_function_ids));
  const functionsMatch =
    !clipboardEmpty &&
    dutyFunctions.filter(d => clipboardFunctionIds.has(d.id)).every(d => !d.skill_id || skillIds.includes(d.skill_id));

  const canPaste = functionsMatch && scenario1Allowed;

  let disallowedReason = null;
  if (!functionsMatch) {
    disallowedReason = I18n.t('duty_planner.context_menu.paste.disallowed_reasons.missing_required_skill');
  } else if (!scenario1Allowed) {
    const start = moment(clipboardStartTime).format('HH:mm');
    const end = moment(clipboardEndTime).format('HH:mm');
    disallowedReason = I18n.t('duty_planner.context_menu.paste.disallowed_reasons.different_timeslot', { start, end });
  }

  if (disallowedReason) disallowedReason = `(${disallowedReason})`;
  if (clipboardEmpty) disallowedReason = null;

  return (
    <Dropdown.Item onClick={pasteInterval} disabled={!canPaste}>
      <IconPaste fixedWidth />
      {I18n.t('paste')} {disallowedReason}
    </Dropdown.Item>
  );
};

const ClearIntervalMenuItem = ({ updateInterval }) => {
  const clearInterval = () => updateInterval(emptyInterval);

  return (
    <Dropdown.Item onClick={clearInterval}>
      <span className="text-danger">
        <IconTrash fixedWidth />
        {I18n.t('delete')}
      </span>
    </Dropdown.Item>
  );
};

const AvailabilityCodeSubMenu = ({ updateInterval, availabilityCodes }) => {
  if (availabilityCodes.length === 0) return null;

  const applyAvailabilityCode = code => updateInterval({ availability_code_id: code ? code.id : null });

  return (
    <Dropdown.SubMenu title={I18n.t('activerecord.models.schedule/availability_code.one')}>
      <Dropdown.Item onClick={() => applyAvailabilityCode(null)} key={'none'}>
        <i>{I18n.t('none')}</i>
      </Dropdown.Item>
      <Dropdown.Divider />
      {availabilityCodes.map(code => (
        <Dropdown.Item onClick={() => applyAvailabilityCode(code)} key={code.id}>
          <AvailabilityCodeIcon code={code} /> {code.name}
        </Dropdown.Item>
      ))}
    </Dropdown.SubMenu>
  );
};

const DutyFunctionSubMenu = ({ skillIds, dutyFunctions, updateInterval }) => {
  const applicableDutyFunctions = dutyFunctions.filter(d => !d.skill_id || skillIds.includes(d.skill_id));

  if (applicableDutyFunctions.length === 0) return null;

  const applyDutyFunction = func => {
    const assigned_function_ids = func ? [func.id] : [];
    updateInterval({ assigned_function_ids });
  };

  return (
    <Dropdown.SubMenu title={I18n.t('activerecord.models.standby_duty_function')}>
      <Dropdown.Item onClick={() => applyDutyFunction(null)} key={'none'}>
        <i>{I18n.t('none')}</i>
      </Dropdown.Item>
      <Dropdown.Divider />
      {applicableDutyFunctions.map(func => (
        <Dropdown.Item onClick={() => applyDutyFunction(func)} key={func.id}>
          <DutyFunctionIcon dutyFunction={func} /> {func.name}
        </Dropdown.Item>
      ))}
    </Dropdown.SubMenu>
  );
};

const mapStateToProps = state => ({
  dutyFunctions: standbyDutyFunctionsSelectors.active(state),
  availabilityCodes: availabilityCodeSelectors.selectSchedulableAvailabilityCodes(state),
  schedules: state.combinedSchedules.full
});

const mapDispatchToProps = {
  updateIntervalsInBackend,
  updateIntervalsInMemory,
  fetchSchedule,
  fetchDutyFunctions: standbyDutyFunctionsOperations.fetch,
  fetchAvailabilityCodes: availabilityCodeOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(ContextMenu);
